import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiError, formatDate } from "../lib/apiClient";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "../components/ui/dialog";
import ConfirmDialog from "../components/ConfirmDialog";
import { useConfirm } from "../components/useConfirm";
import { toast } from "sonner";
import {
  Beaker, ArrowLeft, RefreshCw, Plus, BarChart3, Trophy,
  Play, Pause, CheckCircle2, Star, Award, TrendingDown, TrendingUp,
} from "lucide-react";

const TAB_TESTS = "tests";
const TAB_HISTORY = "history";

export default function AdminPromptsAbPage() {
  const navigate = useNavigate();
  const [tab, setTab] = useState(TAB_TESTS);

  return (
    <div className="p-6 max-w-7xl mx-auto">
      <div className="mb-4">
        <Button variant="ghost" size="sm" onClick={() => navigate("/admin/prompts")} className="mb-2" data-testid="back-to-prompts">
          <ArrowLeft className="w-4 h-4 mr-1" /> Volver a prompts
        </Button>
        <h1 className="font-serif text-3xl text-gray-900 mb-1 flex items-center gap-2">
          <Beaker className="w-7 h-7 text-brand-burgundy" />
          Pruebas A/B
        </h1>
        <p className="text-sm text-gray-600">Compara versiones de prompts y promueve el ganador según métricas.</p>
      </div>

      <div className="flex gap-1 border-b border-gray-200 mb-6">
        <TabButton active={tab === TAB_TESTS} onClick={() => setTab(TAB_TESTS)} data-testid="tab-tests">
          <Beaker className="w-4 h-4" /> Pruebas
        </TabButton>
        <TabButton active={tab === TAB_HISTORY} onClick={() => setTab(TAB_HISTORY)} data-testid="tab-history">
          <Award className="w-4 h-4" /> Historial de winners
        </TabButton>
      </div>

      {tab === TAB_TESTS ? <TestsList /> : <WinnersHistory />}
    </div>
  );
}

function TabButton({ active, onClick, children, ...rest }) {
  return (
    <button
      onClick={onClick}
      className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors flex items-center gap-2 ${
        active
          ? "border-brand-burgundy text-brand-burgundy"
          : "border-transparent text-gray-500 hover:text-gray-700"
      }`}
      {...rest}
    >
      {children}
    </button>
  );
}

// ─────────────────────────────────────────────────────────────────────
// Tab: Lista de tests
// ─────────────────────────────────────────────────────────────────────

function TestsList() {
  const [tests, setTests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("");
  const [createOpen, setCreateOpen] = useState(false);
  const [detailTestId, setDetailTestId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = statusFilter ? `?status=${statusFilter}` : "";
      const { data } = await api.get(`/admin/prompts/ab-tests${params}`);
      setTests(data.items || []);
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Error cargando tests");
    } finally {
      setLoading(false);
    }
  }, [statusFilter]);

  useEffect(() => { load(); }, [load]);

  const statusBadge = (s) => {
    const cfg = {
      running: { bg: "bg-emerald-100", fg: "text-emerald-800", icon: Play, label: "Running" },
      paused: { bg: "bg-amber-100", fg: "text-amber-800", icon: Pause, label: "Pausado" },
      completed: { bg: "bg-gray-100", fg: "text-gray-700", icon: CheckCircle2, label: "Completado" },
    }[s] || { bg: "bg-gray-100", fg: "text-gray-700", icon: Play, label: s };
    const Icon = cfg.icon;
    return (
      <span className={`inline-flex items-center gap-1 text-[10px] uppercase tracking-wider px-1.5 py-0.5 ${cfg.bg} ${cfg.fg} rounded-sm border border-current/20 font-semibold`}>
        <Icon className="w-3 h-3" /> {cfg.label}
      </span>
    );
  };

  return (
    <>
      <div className="mb-4 flex flex-col md:flex-row md:items-center md:justify-end gap-2">
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="h-10 border border-gray-300 rounded-sm px-3 text-sm"
          data-testid="ab-status-filter"
        >
          <option value="">Todos los estados</option>
          <option value="running">Running</option>
          <option value="paused">Pausados</option>
          <option value="completed">Completados</option>
        </select>
        <Button variant="outline" onClick={load} disabled={loading}>
          <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
        </Button>
        <Button
          onClick={() => setCreateOpen(true)}
          className="bg-brand-burgundy hover:bg-brand-burgundy-light text-white"
          data-testid="ab-create-btn"
        >
          <Plus className="w-4 h-4 mr-2" /> Nuevo A/B
        </Button>
      </div>

      <div className="space-y-3">
        {loading && tests.length === 0 && (
          <div className="p-8 text-center text-gray-500">Cargando…</div>
        )}
        {!loading && tests.length === 0 && (
          <div className="p-8 text-center text-gray-500 border border-dashed border-gray-200 rounded-sm">
            <Beaker className="w-12 h-12 mx-auto mb-2 text-gray-300" />
            <div>No hay pruebas A/B aún.</div>
            <div className="text-xs text-gray-400 mt-1">Crea una nueva para empezar a comparar versiones de prompts.</div>
          </div>
        )}
        {tests.map((t) => (
          <div
            key={t.id}
            className="border border-gray-200 rounded-sm bg-white p-4 hover:border-brand-burgundy/40 cursor-pointer transition-colors"
            onClick={() => setDetailTestId(t.id)}
            data-testid={`ab-test-${t.id}`}
          >
            <div className="flex items-start justify-between gap-4">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1 flex-wrap">
                  <h3 className="font-medium text-gray-900">{t.name}</h3>
                  {statusBadge(t.status)}
                  {t.winner_label && (
                    <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider px-1.5 py-0.5 bg-brand-cream text-brand-burgundy rounded-sm border border-brand-burgundy/20 font-semibold">
                      <Trophy className="w-3 h-3" /> Ganador: {t.winner_label}
                    </span>
                  )}
                </div>
                <div className="text-xs text-gray-500 flex gap-3 flex-wrap">
                  <span>Prompt: <code className="bg-gray-100 px-1">{t.prompt_key}</code></span>
                  <span>·</span>
                  <span>v{t.variant_a_version || "?"} ({t.split_ratio}%) vs v{t.variant_b_version || "?"} ({(100 - t.split_ratio).toFixed(0)}%)</span>
                  <span>·</span>
                  <span>Iniciado: {formatDate(t.started_at)}</span>
                </div>
              </div>
              <BarChart3 className="w-5 h-5 text-gray-300" />
            </div>
          </div>
        ))}
      </div>

      <CreateAbTestModal
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        onCreated={() => { setCreateOpen(false); load(); }}
      />

      <AbTestDetailModal
        testId={detailTestId}
        onClose={() => setDetailTestId(null)}
        onReload={load}
      />
    </>
  );
}

// ─────────────────────────────────────────────────────────────────────
// Tab: Historial de winners
// ─────────────────────────────────────────────────────────────────────

function WinnersHistory() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [keyFilter, setKeyFilter] = useState("");
  const [prompts, setPrompts] = useState([]);
  const [detailTestId, setDetailTestId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = keyFilter ? `?prompt_key=${encodeURIComponent(keyFilter)}` : "";
      const { data } = await api.get(`/admin/prompts/ab-tests/history/winners${params}`);
      setItems(data.items || []);
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Error cargando historial");
    } finally {
      setLoading(false);
    }
  }, [keyFilter]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    api.get("/admin/prompts").then(({ data }) => setPrompts(data.items || [])).catch(() => {});
  }, []);

  return (
    <>
      <div className="mb-4 flex items-center gap-2 flex-wrap">
        <select
          value={keyFilter}
          onChange={(e) => setKeyFilter(e.target.value)}
          className="h-10 border border-gray-300 rounded-sm px-3 text-sm bg-white"
          data-testid="history-key-filter"
        >
          <option value="">Todos los prompts</option>
          {prompts.map((p) => (
            <option key={p.prompt_key} value={p.prompt_key}>{p.name}</option>
          ))}
        </select>
        <Button variant="outline" onClick={load} disabled={loading}>
          <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
        </Button>
      </div>

      {loading && items.length === 0 && (
        <div className="p-8 text-center text-gray-500">Cargando…</div>
      )}
      {!loading && items.length === 0 && (
        <div className="p-8 text-center text-gray-500 border border-dashed border-gray-200 rounded-sm">
          <Award className="w-12 h-12 mx-auto mb-2 text-gray-300" />
          <div>No hay tests promovidos todavía.</div>
          <div className="text-xs text-gray-400 mt-1">Cuando promuevas un winner de un A/B, aparecerá acá con sus deltas.</div>
        </div>
      )}

      {/* Timeline */}
      <div className="relative pl-6 border-l-2 border-gray-200 space-y-6" data-testid="winners-timeline">
        {items.map((it) => (
          <div key={it.test_id} className="relative">
            <div className="absolute -left-[31px] top-1 w-6 h-6 rounded-full bg-brand-burgundy flex items-center justify-center ring-4 ring-white">
              <Trophy className="w-3 h-3 text-white" />
            </div>
            <div
              className="bg-white border border-gray-200 rounded-sm p-4 hover:border-brand-burgundy/40 cursor-pointer transition-colors"
              onClick={() => setDetailTestId(it.test_id)}
              data-testid={`winner-row-${it.test_id}`}
            >
              <div className="flex items-start justify-between gap-4 flex-wrap mb-2">
                <div>
                  <h3 className="font-medium text-gray-900">{it.name}</h3>
                  <div className="text-xs text-gray-500 flex gap-2 flex-wrap mt-1">
                    <code className="bg-gray-100 px-1">{it.prompt_key}</code>
                    <span>·</span>
                    <span>{formatDate(it.ended_at)}</span>
                    <span>·</span>
                    <span>{it.total_samples} samples</span>
                  </div>
                </div>
                <div className="text-right">
                  <div className="text-xs text-gray-500">Ganador</div>
                  <div className="text-sm font-semibold text-brand-burgundy">
                    Variant {it.winner_label} (v{it.winner_version})
                  </div>
                  <div className="text-[10px] text-gray-400">{it.winner_model || "—"}</div>
                </div>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-xs">
                <DeltaBadge
                  label="Latencia"
                  delta={it.delta_latency_ms}
                  format={(v) => `${v >= 0 ? "+" : ""}${Math.round(v)} ms`}
                  betterWhen="lower"
                />
                <DeltaBadge
                  label="Costo"
                  delta={it.delta_cost_usd}
                  format={(v) => `${v >= 0 ? "+" : ""}$${v.toFixed(4)}`}
                  betterWhen="lower"
                />
                <DeltaBadge
                  label="Rating"
                  delta={it.delta_rating}
                  format={(v) => `${v >= 0 ? "+" : ""}${v.toFixed(2)}`}
                  betterWhen="higher"
                />
                <div className="text-gray-500">
                  vs <span className="text-gray-700">Variant {it.loser_label}</span> (v{it.loser_version})
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      <AbTestDetailModal
        testId={detailTestId}
        onClose={() => setDetailTestId(null)}
        onReload={load}
      />
    </>
  );
}

function DeltaBadge({ label, delta, format, betterWhen }) {
  if (delta === null || delta === undefined) {
    return (
      <div className="px-2 py-1 bg-gray-50 rounded-sm border border-gray-200">
        <div className="text-[10px] uppercase text-gray-500">{label}</div>
        <div className="text-gray-400">—</div>
      </div>
    );
  }
  const isBetter = betterWhen === "lower" ? delta < 0 : delta > 0;
  const cls = isBetter
    ? "bg-emerald-50 border-emerald-200 text-emerald-900"
    : delta === 0
      ? "bg-gray-50 border-gray-200 text-gray-700"
      : "bg-red-50 border-red-200 text-red-900";
  const Icon = isBetter ? TrendingDown : delta === 0 ? null : TrendingUp;
  return (
    <div className={`px-2 py-1 rounded-sm border ${cls}`}>
      <div className="text-[10px] uppercase opacity-70">{label}</div>
      <div className="font-mono flex items-center gap-1">
        {Icon && <Icon className="w-3 h-3" />}
        {format(delta)}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────
// Modal: crear A/B test
// ─────────────────────────────────────────────────────────────────────

function CreateAbTestModal({ open, onClose, onCreated }) {
  const [prompts, setPrompts] = useState([]);
  const [selectedKey, setSelectedKey] = useState("");
  const [versions, setVersions] = useState([]);
  const [variantA, setVariantA] = useState("");
  const [variantB, setVariantB] = useState("");
  const [name, setName] = useState("");
  const [split, setSplit] = useState(50);
  const [creating, setCreating] = useState(false);

  useEffect(() => {
    if (!open) return;
    api.get("/admin/prompts").then(({ data }) => setPrompts(data.items || [])).catch(() => {});
  }, [open]);

  useEffect(() => {
    if (!selectedKey) { setVersions([]); return; }
    api.get(`/admin/prompts/${selectedKey}/history`)
      .then(({ data }) => setVersions(data.items || []))
      .catch(() => setVersions([]));
  }, [selectedKey]);

  const create = async () => {
    if (!name.trim() || !selectedKey || !variantA || !variantB) {
      toast.error("Completa todos los campos");
      return;
    }
    if (variantA === variantB) {
      toast.error("Los variants deben ser distintos");
      return;
    }
    setCreating(true);
    try {
      await api.post("/admin/prompts/ab-tests", {
        name: name.trim(),
        prompt_key: selectedKey,
        variant_a_id: variantA,
        variant_b_id: variantB,
        split_ratio: parseFloat(split),
      });
      toast.success("Test creado");
      setName(""); setSelectedKey(""); setVariantA(""); setVariantB(""); setSplit(50);
      onCreated();
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Error creando test");
    } finally {
      setCreating(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && !creating && onClose()}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Nueva prueba A/B</DialogTitle>
          <DialogDescription>Selecciona un prompt y 2 versiones distintas para comparar.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3 mt-2">
          <div>
            <Label>Nombre de la prueba</Label>
            <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Ej: Mejorar tasa de aprobación N1" data-testid="ab-create-name" />
          </div>
          <div>
            <Label>Prompt</Label>
            <select
              value={selectedKey}
              onChange={(e) => { setSelectedKey(e.target.value); setVariantA(""); setVariantB(""); }}
              className="h-10 w-full border border-gray-300 rounded-sm px-3 text-sm bg-white"
              data-testid="ab-create-key"
            >
              <option value="">Selecciona…</option>
              {prompts.map((p) => (<option key={p.id} value={p.prompt_key}>{p.name} ({p.prompt_key})</option>))}
            </select>
          </div>
          {selectedKey && versions.length >= 2 && (
            <>
              <div>
                <Label>Variant A</Label>
                <select value={variantA} onChange={(e) => setVariantA(e.target.value)} className="h-10 w-full border border-gray-300 rounded-sm px-3 text-sm bg-white" data-testid="ab-create-variant-a">
                  <option value="">Selecciona…</option>
                  {versions.map((v) => (<option key={v.id} value={v.id}>v{v.version} {v.is_active ? "(activa)" : ""} {v.model ? `· ${v.model}` : ""}</option>))}
                </select>
              </div>
              <div>
                <Label>Variant B</Label>
                <select value={variantB} onChange={(e) => setVariantB(e.target.value)} className="h-10 w-full border border-gray-300 rounded-sm px-3 text-sm bg-white" data-testid="ab-create-variant-b">
                  <option value="">Selecciona…</option>
                  {versions.filter((v) => v.id !== variantA).map((v) => (<option key={v.id} value={v.id}>v{v.version} {v.is_active ? "(activa)" : ""} {v.model ? `· ${v.model}` : ""}</option>))}
                </select>
              </div>
              <div>
                <Label>Split A / B: {split}% / {(100 - split).toFixed(0)}%</Label>
                <input type="range" min="10" max="90" value={split} onChange={(e) => setSplit(parseFloat(e.target.value))} className="w-full" data-testid="ab-create-split" />
              </div>
            </>
          )}
          {selectedKey && versions.length < 2 && (
            <div className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-sm p-2">
              Este prompt solo tiene {versions.length} versión. Crea otra versión primero desde el editor.
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={creating}>Cancelar</Button>
          <Button onClick={create} disabled={creating || !name || !variantA || !variantB} className="bg-brand-burgundy hover:bg-brand-burgundy-light text-white" data-testid="ab-create-submit">
            {creating ? "Creando…" : "Crear test"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ─────────────────────────────────────────────────────────────────────
// Modal: detalle del test con métricas enriquecidas
// ─────────────────────────────────────────────────────────────────────

function AbTestDetailModal({ testId, onClose, onReload }) {
  const [data, setData] = useState(null);
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [acting, setActing] = useState(false);

  // Iter24.d: confirm dialog reutilizable
  const { confirm: confirmAction, dialogProps: confirmDialogProps } = useConfirm();

  const load = useCallback(async () => {
    if (!testId) return;
    setLoading(true);
    try {
      const [d1, d2] = await Promise.all([
        api.get(`/admin/prompts/ab-tests/${testId}`),
        api.get(`/admin/prompts/ab-tests/${testId}/results?limit=50`),
      ]);
      setData(d1.data);
      setResults(d2.data.items || []);
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Error cargando detalle");
    } finally {
      setLoading(false);
    }
  }, [testId]);

  useEffect(() => { if (testId) load(); else { setData(null); setResults([]); } }, [testId, load]);

  const updateStatus = async (status) => {
    setActing(true);
    try {
      await api.patch(`/admin/prompts/ab-tests/${testId}`, { status });
      toast.success(`Test ${status}`);
      load(); onReload?.();
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Error");
    } finally {
      setActing(false);
    }
  };

  const [promotePreview, setPromotePreview] = useState(null); // {label, analysis} or null

  const promote = async (label) => {
    setActing(true);
    try {
      const { data: analysis } = await api.get(
        `/admin/prompts/ab-tests/${testId}/promote-preview?variant_label=${label}`,
      );
      if (analysis.safe) {
        // No warnings — confirm con modal y promover directo
        const ok = await confirmAction({
          title: `Promover Variant ${label}`,
          description: `¿Promover variant ${label} como activo? El test se marcará como completado y la versión ganadora pasará a ser la activa.`,
          confirmLabel: `Sí, promover ${label}`,
          testIdPrefix: `promote-confirm-${label}`,
        });
        if (!ok) {
          setActing(false);
          return;
        }
        await api.post(`/admin/prompts/ab-tests/${testId}/promote`, { variant_label: label });
        toast.success(`Variant ${label} promovido como activo`);
        load(); onReload?.();
      } else {
        // Hay warnings — abrir modal de override
        setPromotePreview({ label, analysis });
      }
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Error promoviendo");
    } finally {
      setActing(false);
    }
  };

  const promoteConfirmOverride = async () => {
    if (!promotePreview) return;
    setActing(true);
    try {
      await api.post(`/admin/prompts/ab-tests/${testId}/promote`, {
        variant_label: promotePreview.label,
        confirm_override: true,
      });
      toast.success(`Variant ${promotePreview.label} promovido (override aplicado)`);
      setPromotePreview(null);
      load(); onReload?.();
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Error promoviendo");
    } finally {
      setActing(false);
    }
  };

  const rate = async (resultId, rating) => {
    try {
      await api.post(`/admin/prompts/ab-results/${resultId}/rate`, { rating });
      toast.success(`Rating ${rating}/5 guardado`);
      load();
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Error guardando rating");
    }
  };

  if (!testId) return null;

  const test = data?.test;
  const metrics = data?.metrics || {};
  const significance = data?.significance || {};

  return (
    <Dialog open={!!testId} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-5xl max-h-[92vh] overflow-y-auto">
        {/* a11y: DialogTitle siempre presente (incluso en loading) */}
        <DialogHeader className={test ? "" : "sr-only"}>
          <DialogTitle>{test?.name || "Detalle del A/B test"}</DialogTitle>
          {test && (
            <DialogDescription>
              <code>{test.prompt_key}</code> · {test.status} · Split configurado {test.split_ratio}/{(100 - test.split_ratio).toFixed(0)} · {data?.total_samples || 0} muestras totales
            </DialogDescription>
          )}
          {!test && <DialogDescription>Cargando detalles…</DialogDescription>}
        </DialogHeader>
        {loading && <div className="p-6 text-center text-gray-500">Cargando…</div>}
        {test && (
          <>
            {(significance.latency_ms?.t !== null || significance.admin_rating?.t !== null) && (
              <div className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-2">
                <SignificanceBadge title="Latencia" sig={significance.latency_ms} unit="ms" />
                <SignificanceBadge title="Rating admin" sig={significance.admin_rating} unit="/5" />
              </div>
            )}

            {/* Métricas side-by-side */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
              {["A", "B"].map((label) => {
                const m = metrics[label] || {};
                const isWinner = test.winner_label === label;
                return (
                  <VariantMetricsCard
                    key={label}
                    label={label}
                    m={m}
                    isWinner={isWinner}
                    splitConfigured={label === "A" ? test.split_ratio : (100 - test.split_ratio)}
                    canPromote={test.status !== "completed"}
                    acting={acting}
                    onPromote={() => promote(label)}
                  />
                );
              })}
            </div>

            {/* Acciones del test */}
            <div className="mt-4 flex gap-2 flex-wrap">
              {test.status === "running" && (
                <Button size="sm" variant="outline" onClick={() => updateStatus("paused")} disabled={acting} data-testid="action-pause">
                  <Pause className="w-4 h-4 mr-1" /> Pausar
                </Button>
              )}
              {test.status === "paused" && (
                <Button size="sm" variant="outline" onClick={() => updateStatus("running")} disabled={acting} data-testid="action-resume">
                  <Play className="w-4 h-4 mr-1" /> Reanudar
                </Button>
              )}
              {test.status !== "completed" && (
                <Button size="sm" variant="ghost" onClick={() => updateStatus("completed")} disabled={acting} data-testid="action-finalize">
                  <CheckCircle2 className="w-4 h-4 mr-1" /> Finalizar sin ganador
                </Button>
              )}
            </div>

            {/* Resultados detallados con rating */}
            {results.length > 0 && (
              <div className="mt-6">
                <h4 className="text-sm font-semibold text-gray-700 mb-2">Resultados ({results.length})</h4>
                <div className="border border-gray-200 rounded-sm overflow-hidden max-h-[300px] overflow-y-auto">
                  <table className="w-full text-xs">
                    <thead className="bg-gray-50 sticky top-0">
                      <tr>
                        <th className="text-left p-2 font-medium">Fecha</th>
                        <th className="text-left p-2 font-medium">Variant</th>
                        <th className="text-left p-2 font-medium">Latency</th>
                        <th className="text-left p-2 font-medium">Costo</th>
                        <th className="text-left p-2 font-medium">Rating</th>
                      </tr>
                    </thead>
                    <tbody>
                      {results.map((r) => (
                        <tr key={r.id} className="border-t border-gray-100 hover:bg-gray-50">
                          <td className="p-2 text-gray-700">{formatDate(r.created_at)}</td>
                          <td className="p-2">
                            <span className={`px-1.5 py-0.5 rounded-sm ${r.variant_label === "A" ? "bg-blue-100 text-blue-800" : "bg-purple-100 text-purple-800"}`}>{r.variant_label}</span>
                            {r.error && <span className="ml-1 text-[10px] text-red-700">⚠ error</span>}
                          </td>
                          <td className="p-2">{r.latency_ms ? `${r.latency_ms}ms` : "—"}</td>
                          <td className="p-2">{r.cost_usd ? `$${r.cost_usd.toFixed(4)}` : "—"}</td>
                          <td className="p-2"><RatingStars current={r.admin_rating} onRate={(n) => rate(r.id, n)} /></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            <DialogFooter className="mt-4">
              <Button variant="outline" onClick={onClose}>Cerrar</Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>

      {/* P2: Modal de advertencia pre-promoción A/B */}
      <Dialog
        open={Boolean(promotePreview)}
        onOpenChange={(o) => { if (!o) setPromotePreview(null); }}
      >
        <DialogContent className="max-w-xl" data-testid="promote-warning-modal">
          <DialogHeader>
            <DialogTitle className="text-amber-700">
              ⚠ Advertencias para promover Variant {promotePreview?.label}
            </DialogTitle>
            <DialogDescription>
              Detectamos trade-offs en esta promoción. Revísalos antes de confirmar.
            </DialogDescription>
          </DialogHeader>

          {promotePreview && (
            <div className="space-y-3 mt-2">
              {(promotePreview.analysis.warnings || []).map((w, i) => (
                <div
                  key={i}
                  className="border-l-4 border-amber-500 bg-amber-50 p-3 rounded-sm"
                  data-testid={`promote-warning-${w.code}`}
                >
                  <p className="text-xs font-mono font-semibold text-amber-900 mb-1">{w.code}</p>
                  <p className="text-sm text-gray-800">{w.message}</p>
                </div>
              ))}

              <div className="bg-gray-50 p-3 border border-gray-200 rounded-sm text-xs space-y-1">
                <p><strong>Ganador propuesto:</strong> {promotePreview.analysis.winner_label} (vs {promotePreview.analysis.loser_label})</p>
                {promotePreview.analysis.winner_cost_avg !== null && (
                  <p>
                    <strong>Costo medio:</strong> Ganador USD {promotePreview.analysis.winner_cost_avg?.toFixed(6)} ·
                    Perdedor USD {promotePreview.analysis.loser_cost_avg?.toFixed(6)}
                    {promotePreview.analysis.delta_cost_pct !== null && (
                      <span className={promotePreview.analysis.delta_cost_pct > 0 ? "text-rose-700 ml-1" : "text-emerald-700 ml-1"}>
                        (Δ {promotePreview.analysis.delta_cost_pct > 0 ? "+" : ""}{promotePreview.analysis.delta_cost_pct}%)
                      </span>
                    )}
                  </p>
                )}
                {promotePreview.analysis.p_value_rating !== null && (
                  <p>
                    <strong>Welch p-value rating:</strong> {promotePreview.analysis.p_value_rating?.toFixed(4)}
                    {" "}
                    <span className={promotePreview.analysis.rating_significant ? "text-emerald-700" : "text-amber-700"}>
                      {promotePreview.analysis.rating_significant ? "(significativo)" : "(no significativo)"}
                    </span>
                  </p>
                )}
              </div>
            </div>
          )}

          <DialogFooter className="mt-4">
            <Button
              variant="outline"
              onClick={() => setPromotePreview(null)}
              data-testid="promote-cancel"
            >
              Cancelar
            </Button>
            <Button
              className="bg-amber-600 hover:bg-amber-700 text-white"
              disabled={acting}
              onClick={promoteConfirmOverride}
              data-testid="promote-confirm-override"
            >
              {acting ? "Promoviendo…" : "Promover de todos modos"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <ConfirmDialog {...confirmDialogProps} />
    </Dialog>
  );
}

function VariantMetricsCard({ label, m, isWinner, splitConfigured, canPromote, acting, onPromote }) {
  const share = m.share_pct;
  const shareDelta = share !== undefined && splitConfigured !== undefined ? (share - splitConfigured) : null;
  return (
    <div
      className={`border rounded-sm p-4 ${
        isWinner ? "border-brand-burgundy bg-brand-cream/50" : "border-gray-200 bg-white"
      }`}
      data-testid={`variant-card-${label}`}
    >
      <div className="flex items-center justify-between mb-3">
        <h4 className="font-semibold text-gray-900 flex items-center gap-2">
          Variant {label}
          {isWinner && <Trophy className="w-4 h-4 text-brand-burgundy" />}
        </h4>
        <span className="text-xs text-gray-500">
          {m.samples || 0} muestras
          {share !== undefined && (
            <> · {share?.toFixed?.(1) ?? share}%
              {shareDelta !== null && Math.abs(shareDelta) > 5 && (
                <span className="ml-1 text-amber-700">(esperado {splitConfigured?.toFixed?.(0)}%)</span>
              )}
            </>
          )}
        </span>
      </div>

      {/* Latencia */}
      <Section title="Latencia">
        <MRow label="Media" value={fmtMs(m.latency_ms_avg)} />
        <MRow label="Mediana (p50)" value={fmtMs(m.latency_ms_p50)} />
        <MRow label="p95" value={fmtMs(m.latency_ms_p95)} />
        <MRow label="p99" value={fmtMs(m.latency_ms_p99)} />
      </Section>

      {/* Costo */}
      <Section title="Costo">
        <MRow label="Total" value={fmtCost(m.cost_usd_total)} />
        <MRow label="Promedio" value={fmtCost(m.cost_usd_avg)} />
        <MRow label="$ / 1k tokens" value={fmtCost(m.cost_per_1k_tokens)} />
      </Section>

      {/* Tokens */}
      <Section title="Tokens (avg)">
        <MRow label="In" value={fmtNum(m.tokens_in_avg)} />
        <MRow label="Out" value={fmtNum(m.tokens_out_avg)} />
      </Section>

      {/* Rating */}
      <Section title="Rating admin">
        <MRow label="Promedio" value={m.admin_rating_avg ? `${m.admin_rating_avg.toFixed(2)} / 5 (${m.admin_rating_count})` : "—"} />
        {m.admin_rating_distribution && Object.values(m.admin_rating_distribution).some((v) => v > 0) && (
          <RatingHistogram dist={m.admin_rating_distribution} />
        )}
      </Section>

      {/* NPS */}
      <Section title="NPS usuario">
        <MRow label="Score NPS" value={m.user_nps_score !== null ? m.user_nps_score?.toFixed?.(1) : "—"} />
        <MRow label="Promedio (0-10)" value={m.user_nps_avg ? `${m.user_nps_avg.toFixed(1)} (${m.user_nps_count})` : "—"} />
        {(m.user_nps_count || 0) > 0 && (
          <NpsBar promoters={m.user_nps_promoters} passives={m.user_nps_passives} detractors={m.user_nps_detractors} />
        )}
      </Section>

      {/* Calidad técnica */}
      <Section title="Calidad técnica">
        <MRow label="Tasa de éxito" value={m.success_rate_pct !== null ? `${m.success_rate_pct?.toFixed?.(1)}%` : "—"} />
        <MRow label="Errores" value={m.errors !== undefined ? m.errors : "—"} />
      </Section>

      {/* Sparkline */}
      {Array.isArray(m.daily_samples) && m.daily_samples.length > 0 && (
        <Section title="Muestras por día">
          <Sparkline points={m.daily_samples} />
        </Section>
      )}

      {canPromote && (
        <Button
          size="sm"
          variant="outline"
          onClick={onPromote}
          disabled={acting}
          className="mt-3 w-full text-brand-burgundy border-brand-burgundy/30 hover:bg-brand-cream"
          data-testid={`promote-${label}`}
        >
          <Trophy className="w-4 h-4 mr-1" /> Promover {label}
        </Button>
      )}
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div className="mt-3">
      <div className="text-[10px] uppercase tracking-wider text-gray-500 mb-1">{title}</div>
      <div className="space-y-0.5 text-xs text-gray-700">{children}</div>
    </div>
  );
}

function MRow({ label, value }) {
  return (
    <div className="flex justify-between items-baseline">
      <span className="text-gray-500">{label}</span>
      <span className="font-mono text-gray-900">{value}</span>
    </div>
  );
}

function RatingHistogram({ dist }) {
  const max = Math.max(1, ...Object.values(dist));
  return (
    <div className="mt-1 flex items-end gap-1 h-10">
      {[1, 2, 3, 4, 5].map((n) => {
        const c = dist[String(n)] || 0;
        const h = (c / max) * 100;
        return (
          <div key={n} className="flex-1 flex flex-col items-center" title={`${n}★: ${c}`}>
            <div className="w-full bg-amber-400 rounded-sm" style={{ height: `${h}%`, minHeight: c > 0 ? "2px" : "0" }} />
            <span className="text-[9px] text-gray-500 mt-0.5">{n}★</span>
          </div>
        );
      })}
    </div>
  );
}

function NpsBar({ promoters = 0, passives = 0, detractors = 0 }) {
  const total = promoters + passives + detractors || 1;
  const p = (promoters / total) * 100;
  const pa = (passives / total) * 100;
  const d = (detractors / total) * 100;
  return (
    <div className="mt-1">
      <div className="h-2 w-full rounded-sm overflow-hidden flex">
        <div className="bg-emerald-500" style={{ width: `${p}%` }} title={`Promoters: ${promoters}`} />
        <div className="bg-amber-400" style={{ width: `${pa}%` }} title={`Passives: ${passives}`} />
        <div className="bg-red-500" style={{ width: `${d}%` }} title={`Detractors: ${detractors}`} />
      </div>
      <div className="flex justify-between text-[9px] text-gray-500 mt-0.5">
        <span>P {promoters}</span><span>Pa {passives}</span><span>D {detractors}</span>
      </div>
    </div>
  );
}

function Sparkline({ points }) {
  if (!points.length) return null;
  const w = 200, h = 28;
  const max = Math.max(...points.map((p) => p.count), 1);
  const step = points.length > 1 ? w / (points.length - 1) : w;
  const path = points.map((p, i) => `${i === 0 ? "M" : "L"} ${i * step} ${h - (p.count / max) * (h - 2) - 1}`).join(" ");
  return (
    <svg width={w} height={h} className="overflow-visible">
      <path d={path} fill="none" stroke="currentColor" className="text-brand-burgundy" strokeWidth="1.5" />
      {points.map((p, i) => (
        <circle key={i} cx={i * step} cy={h - (p.count / max) * (h - 2) - 1} r="1.5" className="fill-brand-burgundy">
          <title>{p.date}: {p.count}</title>
        </circle>
      ))}
    </svg>
  );
}

function SignificanceBadge({ title, sig, unit }) {
  if (!sig || sig.t === null || sig.t === undefined) {
    return (
      <div className="px-3 py-2 border border-gray-200 bg-gray-50 rounded-sm text-xs text-gray-500">
        <div className="font-medium text-gray-700">{title}</div>
        <div>{sig?.note || "Muestra insuficiente"}</div>
      </div>
    );
  }
  const cls = sig.significant_95
    ? "border-emerald-300 bg-emerald-50 text-emerald-900"
    : "border-gray-200 bg-gray-50 text-gray-700";
  return (
    <div className={`px-3 py-2 border rounded-sm text-xs ${cls}`}>
      <div className="font-medium flex items-center gap-1">
        {title} — Welch&apos;s t-test
        {sig.significant_95 && <span className="text-[10px] px-1 py-0.5 bg-emerald-600 text-white rounded-sm">95% sig.</span>}
      </div>
      <div className="flex gap-3 mt-1 font-mono">
        <span>t = {sig.t}</span>
        <span>df = {sig.df}</span>
        <span>p ≈ {sig.p_approx}</span>
      </div>
      {sig.mean_a !== undefined && (
        <div className="text-[10px] text-gray-600 mt-1">
          mean(A) = {sig.mean_a}{unit} · mean(B) = {sig.mean_b}{unit}
        </div>
      )}
    </div>
  );
}

function RatingStars({ current, onRate }) {
  return (
    <div className="flex gap-0.5">
      {[1, 2, 3, 4, 5].map((n) => (
        <button
          key={n}
          onClick={() => onRate(n)}
          className={`text-xs ${(current || 0) >= n ? "text-amber-500" : "text-gray-300 hover:text-amber-400"}`}
          title={`${n}/5`}
        >
          <Star className="w-3 h-3" fill={(current || 0) >= n ? "currentColor" : "none"} />
        </button>
      ))}
    </div>
  );
}

// helpers
function fmtMs(v) { return v ? `${Math.round(v)} ms` : "—"; }
function fmtCost(v) { return v ? `$${Number(v).toFixed(4)}` : "—"; }
function fmtNum(v) { return v ? Math.round(v) : "—"; }
